import { Injectable, inject } from '@angular/core';
import { CategoriaGrupo, IQueryParam } from 'modelos/src';
import {
  ConfigAsignacionEntidad,
  GrupoEntidadRegistry,
} from './grupo-entidad.registry';

/**
 * Asignación en masa de un grupo a las entidades que matchean un filtro
 * (usado por el asignador por tabla y por el asignador por polígono en el mapa).
 * Reusa el registry: no conoce luminaria/puesta.
 *
 * Multi-FK (idsGrupos): agrega/quita sin pisar (read-modify-write por entidad).
 * Single-FK (idGrupo): setea/limpia. Cada `actualizar` pega al PUT del service
 * de la entidad → dispara el sync de config deseada (reconciliador). Por eso NO
 * se escribe Mongo directo.
 */
@Injectable({ providedIn: 'root' })
export class AsignacionGrupoService {
  private registry = inject(GrupoEntidadRegistry);

  private patch(
    cfg: ConfigAsignacionEntidad,
    e: Record<string, unknown>,
    idGrupo: string,
    accion: 'asignar' | 'quitar',
  ): Record<string, unknown> {
    const campo = cfg.campoFk;
    if (cfg.fkMulti) {
      const ids = new Set(cfg.gruposDe(e));
      if (accion === 'asignar') ids.add(idGrupo);
      else ids.delete(idGrupo);
      return { [campo]: [...ids] };
    }
    return { [campo]: accion === 'asignar' ? idGrupo : null };
  }

  /** Cuenta cuántas entidades matchean el filtro (para preview). */
  public async contar(
    categoria: CategoriaGrupo,
    filter: Record<string, unknown>,
  ): Promise<number> {
    const cfg = this.registry.get(categoria);
    if (!cfg) return 0;
    const res = await cfg.listar({
      filter: JSON.stringify(filter),
      onlyTotalCount: true,
      includeChildren: true,
    } as IQueryParam);
    return res?.totalCount ?? 0;
  }

  /**
   * Asigna/quita el grupo a TODAS las entidades del filtro. Devuelve cuántas se
   * actualizaron. Trae solo _id + FK.
   */
  public async bulk(
    categoria: CategoriaGrupo,
    idGrupo: string,
    filter: Record<string, unknown>,
    accion: 'asignar' | 'quitar',
  ): Promise<number> {
    const cfg = this.registry.get(categoria);
    if (!cfg) throw new Error(`Categoría no soportada: ${categoria}`);
    const res = await cfg.listar({
      filter: JSON.stringify(filter),
      select: `_id ${cfg.campoFk}`,
      unlimited: true,
      includeChildren: true,
    } as IQueryParam);
    let n = 0;
    for (const e of res.datos as Record<string, unknown>[]) {
      await cfg.actualizar(
        e['_id'] as string,
        this.patch(cfg, e, idGrupo, accion),
      );
      n++;
    }
    return n;
  }

  /** Entidades (con _id, FK y campos de `select`) que caen dentro del polígono. */
  public async entidadesEnPoligono(
    categoria: CategoriaGrupo,
    polygon: GeoJSON.Polygon,
  ): Promise<Record<string, unknown>[]> {
    const cfg = this.registry.get(categoria);
    const filtro = this.filtroPoligono(categoria, polygon);
    if (!cfg || !filtro) return [];
    const res = await cfg.listar({
      filter: JSON.stringify(filtro),
      select: cfg.select,
      unlimited: true,
      includeChildren: true,
    } as IQueryParam);
    return (res?.datos ?? []) as Record<string, unknown>[];
  }

  /** Asigna/quita el grupo a una LISTA concreta de entidades (ya traídas). */
  public async bulkEntidades(
    categoria: CategoriaGrupo,
    idGrupo: string,
    entidades: Record<string, unknown>[],
    accion: 'asignar' | 'quitar',
  ): Promise<number> {
    const cfg = this.registry.get(categoria);
    if (!cfg) throw new Error(`Categoría no soportada: ${categoria}`);
    let n = 0;
    for (const e of entidades) {
      await cfg.actualizar(
        e['_id'] as string,
        this.patch(cfg, e, idGrupo, accion),
      );
      n++;
    }
    return n;
  }

  /** Filtro geo $geoWithin sobre el geoField de la categoría (polígono). */
  public filtroPoligono(
    categoria: CategoriaGrupo,
    polygon: GeoJSON.Polygon,
    extra?: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    const cfg = this.registry.get(categoria);
    if (!cfg?.geoField) return undefined;
    return {
      ...(extra ?? {}),
      [cfg.geoField]: { $geoWithin: { $geometry: polygon } },
    };
  }
}
